import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon, InputWithIcon, toast } from '@livable/saas-react-components';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { PhoneNumberField } from 'src/components/shared/PhoneNumberField';
import { useStore } from 'src/stores';
import { mfaPhoneSchema, mfaSmsCodeInitialValues, mfaSmsCodeSchema } from 'src/utils/formValidation/MFA';
import useStyles from './styles';
const ChangeNumber = observer(props => {
  const insideModal = props.insideModal,
    phoneNumber = props.phoneNumber,
    setPhoneConfirmed = props.setPhoneConfirmed;
  const classes = useStyles({
    insideModal
  });
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const codeIsSent = store.account.userProfile.codeIsSent;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    clicked = _useState2[0],
    setIsClicked = _useState2[1];
  const sendCodeFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      phone: phoneNumber !== null && phoneNumber !== void 0 ? phoneNumber : ''
    },
    validationSchema: mfaPhoneSchema,
    onSubmit: async values => {
      const data = {
        phone: values.phone
      };
      const resp = await store.account.updatePhoneNumberAsync.run(data);
      if (resp && resp.isSuccessful) {
        store.account.userProfile.setCodeIsSent(true);
        store.account.userProfile.updatePhone(data.phone);
        toast('info', {
          body: [t('mfa.code_sent_to_phone')]
        });
      }
    }
  });
  const confirmCodeFormik = useFormik({
    initialValues: mfaSmsCodeInitialValues,
    validationSchema: mfaSmsCodeSchema
  });
  const confirmPhoneNumber = async data => {
    const resp = await store.account.confirmPhoneNumberAsync.run(data);
    return resp;
  };
  useEffect(() => {
    if (clicked) {
      confirmPhoneNumber({
        code: confirmCodeFormik.values.code.replaceAll('-', '')
      }).then(resp => {
        if (resp && resp.isSuccessful) {
          setPhoneConfirmed(true);
          store.account.userProfile.setAuthIsConfirmed(true);
          toast('success', {
            body: [t('mfa.phone_verified')]
          });
        }
      }).finally(() => setIsClicked(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicked]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(codeIsSent ? classes.success : '')
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: sendCodeFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 10,
    rowSpacing: 18
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, phoneNumber ? t('mfa.code_will_be_sent_to_personal_phone') : t('mfa.enter_phone_number_auth'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(PhoneNumberField, {
    id: "phone",
    name: "phone",
    label: t('general.phone_number'),
    countryLabel: t('mfa.country'),
    value: sendCodeFormik.values.phone,
    onChange: e164 => sendCodeFormik.setFieldValue('phone', e164),
    onBlur: () => sendCodeFormik.setFieldTouched('phone', true),
    disabled: store.account.updatePhoneNumberAsync.inProgress || store.account.mfaFlowStore.timeCounted || store.account.mfaFlowStore.mfaIsActivated,
    error: sendCodeFormik.touched.phone && Boolean(sendCodeFormik.errors.phone),
    helperText: sendCodeFormik.touched.phone && sendCodeFormik.errors.phone ? t('mfa.invalid_phone') : t('mfa.sms_intl_notice')
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: store.account.updatePhoneNumberAsync.inProgress,
    type: "submit",
    variant: "text",
    'custom-attribute': '280-change-number'
  }, codeIsSent ? t('profile_settings.re-send_code') : t('profile_settings.send_confirmation_code')))))), codeIsSent ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(store.account.userProfile.authIsConfirmed ? classes.success : '')
  }, /*#__PURE__*/React.createElement("form", {
    id: "confirm",
    onSubmit: confirmCodeFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 10,
    rowSpacing: 18
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('mfa.code_was_sent'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "shield-keyhole"
    })
  }, /*#__PURE__*/React.createElement(InputMask, {
    onBlur: e => {
      if (e.target.value) confirmCodeFormik.handleBlur(e);
    },
    onChange: confirmCodeFormik.handleChange,
    disabled: store.account.confirmPhoneNumberAsync.inProgress,
    mask: "99-99-99",
    value: confirmCodeFormik.values.code
  }, inputProps => /*#__PURE__*/React.createElement(TextField, _extends({}, inputProps, {
    disabled: store.account.confirmPhoneNumberAsync.inProgress,
    fullWidth: true,
    helperText: confirmCodeFormik.touched.code && t(confirmCodeFormik.errors.code),
    id: "code",
    label: t('mfa.enter_code_auth'),
    name: "code",
    type: "tel",
    variant: "outlined"
  }))))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 7
  }, /*#__PURE__*/React.createElement(Button, {
    form: "confirm",
    onClick: () => setIsClicked(true),
    type: "submit",
    variant: "text",
    'custom-attribute': '281-change-number'
  }, t('buttons.confirm')))))) : null);
});
export default ChangeNumber;